/**
 * 경제 — 필지 모델, 시설 생산, 서비스 탐색, 거래, 습격, 플레이어 지출.
 * 프로토타입 v0.4 로직 이식 (수치는 @villager/shared/constants).
 */
import {
  ECONOMY,
  FRONT,
  PARCEL_BUILDS,
  SINK,
  WORLD,
  type BuildId,
  type BuildKind,
  type ParcelType,
} from '@villager/shared';
import { colX, rowZ } from './coords.js';
import type { Cell, Dest, Parcel } from './state.js';
import type { World } from './sim.js';

const { IN } = WORLD;

export function newParcel(type: ParcelType, owner: string): Parcel {
  const cells: Cell[][] = [];
  for (let i = 0; i < IN; i++) {
    const row: Cell[] = [];
    for (let j = 0; j < IN; j++) row.push({ b: null, stock: 0, prodT: 0 });
    cells.push(row);
  }
  return { type, cells, paralyzed: 0, owner };
}

function kindOf(type: ParcelType, b: BuildId): BuildKind {
  return PARCEL_BUILDS[type][b].kind;
}

/** 해당 kind 시설 개수. */
export function parcelCount(p: Parcel, kind: BuildKind): number {
  let n = 0;
  for (let i = 0; i < IN; i++) {
    for (let j = 0; j < IN; j++) {
      const b = p.cells[i]![j]!.b;
      if (b && kindOf(p.type, b) === kind) n++;
    }
  }
  return n;
}

/** 해당 kind 시설 셀들(needStock이면 재고>0만). */
export function parcelServiceCells(p: Parcel, kind: BuildKind, needStock: boolean): Cell[] {
  const out: Cell[] = [];
  for (let i = 0; i < IN; i++) {
    for (let j = 0; j < IN; j++) {
      const c = p.cells[i]![j]!;
      if (c.b && kindOf(p.type, c.b) === kind && (!needStock || c.stock > 0)) out.push(c);
    }
  }
  return out;
}

/**
 * 서비스 제공 필지 탐색 (월드 좌표 반환). 전선 이남(왕국)만.
 * kind='train'이면 던전+마물둥지 보유, 그 외엔 마을+해당 서비스 셀.
 */
export function findParcelService(
  w: World,
  kind: BuildKind | 'train',
  needStock: boolean,
): Dest | null {
  for (let c = 0; c < WORLD.GW; c++) {
    for (let r = w.frontRow; r < WORLD.GD; r++) {
      const t = w.grid[c]![r]!;
      if (!t.parcel || t.parcel.paralyzed > 0) continue;
      const want: ParcelType = kind === 'train' ? 'dungeon' : 'village';
      if (t.parcel.type !== want) continue;
      if (kind === 'train') {
        if (parcelCount(t.parcel, 'den') > 0) return { x: colX(c), z: rowZ(r), c, r };
        continue;
      }
      if (parcelServiceCells(t.parcel, kind, needStock).length > 0) {
        return { x: colX(c), z: rowZ(r), c, r };
      }
    }
  }
  return null;
}

/** 마을 시설 생산 틱 (프로토타입: 1초마다 rate 카운트로 stock 증가). */
export function production(w: World, dt: number): void {
  w.prodT += dt;
  if (w.prodT <= 1) return;
  w.prodT = 0;
  for (let c = 0; c < WORLD.GW; c++) {
    for (let r = 0; r < WORLD.GD; r++) {
      const p = w.grid[c]![r]!.parcel;
      if (!p) continue;
      if (p.paralyzed > 0) {
        p.paralyzed--;
        continue;
      }
      if (p.type !== 'village') continue;
      for (let i = 0; i < IN; i++) {
        for (let j = 0; j < IN; j++) {
          const cell = p.cells[i]![j]!;
          if (!cell.b) continue;
          const d = PARCEL_BUILDS.village[cell.b];
          if (d.stockMax > 0 && cell.stock < d.stockMax) {
            cell.prodT++;
            if (cell.prodT >= d.rate) {
              cell.prodT = 0;
              cell.stock++;
            }
          }
        }
      }
    }
  }
}

/** 필지 내 시설 총 개수 (유지비 계산용). */
function buildCountOf(p: Parcel): number {
  let n = 0;
  for (let i = 0; i < IN; i++) {
    for (let j = 0; j < IN; j++) if (p.cells[i]![j]!.b) n++;
  }
  return n;
}

/**
 * 판매 대금 수령 — 시장 수수료(싱크)를 떼고 **필지 소유자**에게 입금.
 * 모든 매출은 반드시 이 함수를 거친다(수수료 누락 방지).
 */
export function receivePayment(w: World, gross: number, ownerId: string): void {
  const fee = Math.round(gross * SINK.MARKET_FEE_RATE);
  const p = w.player(ownerId);
  p.gold += gross - fee;
  p.sales += gross;
  p.feesPaid += fee;
}

/**
 * 화폐 싱크 — 주기적 보유세 + 시설 유지비 (지시서 3.5).
 * 인플레 방지의 핵심. 필지를 많이 깔수록 고정비가 커진다.
 */
export function taxTick(w: World, dt: number): void {
  w.taxT += dt;
  if (w.taxT < SINK.TAX_INTERVAL) return;
  w.taxT = 0;
  const owned = w.ownedTiles();
  if (!owned.length) return;

  // 소유자별로 집계해서 각자에게 부과 (멀티: 남의 세금을 내가 내지 않는다)
  const byOwner = new Map<string, { tax: number; upkeep: number }>();
  for (const [c, r] of owned) {
    const p = w.grid[c]![r]!.parcel;
    if (!p) continue;
    const acc = byOwner.get(p.owner) ?? { tax: 0, upkeep: 0 };
    acc.tax += SINK.PARCEL_TAX;
    acc.upkeep += buildCountOf(p) * SINK.UPKEEP_PER_BUILD;
    byOwner.set(p.owner, acc);
  }

  for (const [ownerId, { tax, upkeep }] of byOwner) {
    const pl = w.player(ownerId);
    pl.gold -= tax + upkeep;
    pl.taxPaid += tax;
    pl.upkeepPaid += upkeep;
    if (pl.gold < 0) pl.gold = 0; // 파산 처리는 시즌1 스코프 밖 — 0에서 멈춘다
    w.log.emit(w.tick, 'tax', { playerId: ownerId }, undefined, { tax, upkeep });
  }
  const localBill = byOwner.get('me');
  if (localBill) {
    w.feed(`왕국이 보유세 ${localBill.tax}금 + 유지비 ${localBill.upkeep}금을 걷어갔다.`);
  }
}

/** 방어 실패 누적 → 전선 후퇴. 새로 마왕령이 된 행의 필지는 마비된다(파괴 아님). */
function registerDefenseFailure(w: World): void {
  w.defenseFails++;
  if (w.defenseFails < FRONT.RETREAT_FAILS) return;
  w.defenseFails = 0;
  if (w.frontRow >= FRONT.MAX_ROW) return;
  w.frontRow++;
  w.frontDirty = true;
  const lostRow = w.frontRow - 1;
  for (let c = 0; c < WORLD.GW; c++) {
    const t = w.grid[c]![lostRow]!;
    if (t.parcel) {
      t.parcel.paralyzed = Math.max(t.parcel.paralyzed, 40);
      w.markParcelDirty(c, lostRow);
    }
  }
  w.log.emit(w.tick, 'front_retreat', {}, undefined, { frontRow: w.frontRow, lostRow });
  w.feed('방어 실패가 누적됐다. <b>전선이 남쪽으로 밀렸다.</b>', true);
}

/** 주기적 습격: 예고 → RAID_DELAY 뒤 무작위 소유 필지 타격. 망루 수로 방어 판정. */
export function raidTick(w: World, dt: number): void {
  // 예고 스케줄
  w.raidT += dt;
  if (w.raidT >= 60) {
    w.raidT = 0;
    const owned = w.ownedTiles();
    if (owned.length > 0) {
      w.log.emit(w.tick, 'raid', {}, undefined, { phase: 'warn' });
      w.feed('⚠ 마왕군 습격대가 전선을 넘어온다!', true);
      w.pendingRaids.push({ delay: 5 });
    }
  }
  // 예고된 습격 해소
  for (const raid of w.pendingRaids.slice()) {
    raid.delay -= dt;
    if (raid.delay > 0) continue;
    w.pendingRaids.splice(w.pendingRaids.indexOf(raid), 1);
    const owned = w.ownedTiles();
    if (owned.length === 0) continue;
    const [c, r] = w.rng.pick(owned);
    const p = w.grid[c]![r]!.parcel;
    if (!p) continue;
    const towers = p.type === 'village' ? parcelCount(p, 'def') : 0;
    if (w.rng.chance(towers * 0.35)) {
      w.feed(`망루의 화살이 습격대를 쫓아냈다. 필지 (${c},${r})는 무사하다.`);
      w.log.emit(w.tick, 'raid', { parcelId: `${c},${r}` }, undefined, { phase: 'defended' });
    } else {
      p.paralyzed = 25;
      w.feed(
        `습격대가 당신의 ${p.type === 'village' ? '마을' : '던전'} 필지 (${c},${r})를 덮쳤다 — 한동안 마비!`,
        true,
      );
      w.log.emit(w.tick, 'raid', { parcelId: `${c},${r}` }, undefined, { phase: 'hit' });
      w.markParcelDirty(c, r);
      registerDefenseFailure(w);
    }
  }
}

// ── 플레이어 지출(인텐트) ──

/** 로컬 플레이어에게만 피드를 띄운다(멀티에서 남의 행동으로 내 피드가 더러워지지 않게). */
function feedIfLocal(w: World, playerId: string, msg: string, red = false): void {
  if (playerId === 'me') w.feed(msg, red);
}

export function buyParcel(
  w: World,
  c: number,
  r: number,
  type: ParcelType,
  playerId: string,
): boolean {
  const pl = w.player(playerId);
  if (pl.gold < ECONOMY.PARCEL_COST) {
    feedIfLocal(w, playerId, '금이 부족하다.', true);
    return false;
  }
  const t = w.grid[c]?.[r];
  if (!t || t.parcel || r < w.frontRow) return false;
  pl.gold -= ECONOMY.PARCEL_COST;
  t.parcel = newParcel(type, playerId);
  t.forest = false;
  w.markParcelDirty(c, r);
  w.log.emit(w.tick, 'parcel_buy', { playerId, parcelId: `${c},${r}` }, { x: colX(c), z: rowZ(r) }, { type });
  feedIfLocal(w, playerId, `당신이 (${c},${r})를 ${type === 'village' ? '마을' : '던전'} 필지로 매입했다.`);
  return true;
}

export function clearForest(w: World, c: number, r: number, playerId: string): boolean {
  const pl = w.player(playerId);
  if (pl.gold < ECONOMY.FOREST_CLEAR_COST) {
    feedIfLocal(w, playerId, '금이 부족하다.', true);
    return false;
  }
  const t = w.grid[c]?.[r];
  if (!t || !t.forest) return false;
  pl.gold -= ECONOMY.FOREST_CLEAR_COST;
  t.forest = false;
  feedIfLocal(w, playerId, '숲을 개간했다.');
  return true;
}

export function repairParcel(w: World, c: number, r: number, playerId: string): boolean {
  const p = w.grid[c]?.[r]?.parcel;
  if (!p || p.owner !== playerId) return false; // 소유자만 수리
  const pl = w.player(playerId);
  if (pl.gold < ECONOMY.REPAIR_COST) {
    feedIfLocal(w, playerId, '금이 부족하다.', true);
    return false;
  }
  pl.gold -= ECONOMY.REPAIR_COST;
  p.paralyzed = 0;
  w.markParcelDirty(c, r);
  return true;
}

export function buildCell(
  w: World,
  c: number,
  r: number,
  i: number,
  j: number,
  b: BuildId,
  playerId: string,
): boolean {
  const p = w.grid[c]?.[r]?.parcel;
  if (!p || p.owner !== playerId) return false; // 남의 필지엔 못 짓는다
  const defs = PARCEL_BUILDS[p.type] as Record<BuildId, { cost: number; name: string }>;
  const d = defs[b];
  if (!d) return false;
  const pl = w.player(playerId);
  if (pl.gold < d.cost) {
    feedIfLocal(w, playerId, '금이 부족하다.', true);
    return false;
  }
  pl.gold -= d.cost;
  p.cells[i]![j]!.b = b;
  w.markParcelDirty(c, r);
  w.log.emit(w.tick, 'build', { playerId, parcelId: `${c},${r}` }, undefined, { build: b });
  feedIfLocal(w, playerId, `당신의 ${p.type === 'village' ? '마을' : '던전'}에 ${d.name}이(가) 들어섰다.`);
  return true;
}

export function demolishCell(
  w: World,
  c: number,
  r: number,
  i: number,
  j: number,
  playerId: string,
): boolean {
  const p = w.grid[c]?.[r]?.parcel;
  if (!p || p.owner !== playerId) return false;
  p.cells[i]![j]!.b = null;
  w.markParcelDirty(c, r);
  return true;
}
